import { MessageCircleXIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ContentModerationShape,
  contentModerationType,
  comparisonOperators,
  moderationCategories,
  ModerationCategory,
  ComparisonOperator,
  moderationCategoryDescriptions,
  ContentModerationProps,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isContentModerationShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is ContentModerationShape {
  return shape.type === contentModerationType;
}

export class ContentModerationTool extends ToolStateNode {
  static override id = contentModerationType;

  static override uiToolDef = (editor: Editor) => ({
    id: contentModerationType,
    icon: MessageCircleXIcon,
    label: ContentModerationShapeUtil.docs.title,
    kbd: "c",
    defaultColor: ContentModerationShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(contentModerationType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={ContentModeration}
        util={ContentModerationShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: contentModerationType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class ContentModerationShapeUtil extends BindableUtil<ContentModerationShape> {
  static override migrations = createMigrations(contentModerationType);
  static override type = contentModerationType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Content Moderation",
    description:
      "Allows you to only accept/reject content matches your filter level for a given prediction. English Only",
  };

  override getType() {
    return contentModerationType;
  }

  static override props: RecordProps<ContentModerationShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    moderationCategory: T.literalEnum(...moderationCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      moderationCategory: "OK" as const,
      operator: ">=" as const,
      value: 0.9,
      metadata: undefined,
    };
  }

  override component(shape: ContentModerationShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <ContentModeration
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function ContentModeration({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: ContentModerationShape;
  className?: string;
  updateShape: (
    shape: ContentModerationShape,
    props: Partial<ContentModerationProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          ContentModerationShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={ContentModerationShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Moderation Category"
            options={moderationCategories}
            value={shape.props.moderationCategory}
            className="w-[160px] shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                moderationCategory: v as ModerationCategory,
              })
            }
            tooltip={
              <div>
                <div className="text-xs text-slate-dim">
                  The KoalaAI/Text-Moderation moderation category.
                </div>
                <div className="text-xs text-slate-dim">
                  {
                    moderationCategoryDescriptions[
                      shape.props.moderationCategory
                    ]
                  }
                </div>
              </div>
            }
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
